import { useQuery } from '@apollo/client/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { GQL_SCORM_TRACKING_STATS } from '~/gql/scorm/scorm'
import { client } from '~/helpers/apollo'

type Props = {
  searchInput: string
  sessionId: string
  groupId: string
  /**
   * Incrémenté par le bouton « rafraîchir » de la table : les deux requêtes
   * décrivent le même périmètre, elles doivent se recharger ensemble sous peine
   * d'afficher des agrégats plus vieux que la liste qu'ils résument.
   */
  reloadToken: number
}

/** "1h 30m" à partir de secondes ; "< 1m" en dessous de la minute. */
function formatSeconds(seconds: number): string {
  if (seconds < 60) return '< 1m'
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  return [h ? `${h}h` : '', m ? `${m}m` : ''].filter(Boolean).join(' ')
}

/**
 * Tableau de bord des statistiques (#138).
 *
 * Répond à « où en est ma promo ? », question à laquelle la table paginée en
 * dessous ne répond pas : elle décrit un apprenant à la fois, et sa page
 * courante ne fait jamais que dix lignes. Les chiffres viennent donc d'une
 * agrégation serveur sur tout le périmètre filtré.
 */
function StatisticsDashboard({ searchInput, sessionId, groupId, reloadToken }: Props) {
  const { t } = useTranslation()

  const { data, previousData, refetch } = useQuery(GQL_SCORM_TRACKING_STATS, {
    client,
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        searchInput,
        filterBy: sessionId || groupId
          ? {
              ...(sessionId ? { sessionId } : {}),
              ...(groupId ? { groupId } : {}),
            }
          : undefined,
      },
    },
  })

  useEffect(() => {
    if (reloadToken > 0) void refetch()
  }, [reloadToken, refetch])

  // Comme la table : on garde l'affichage précédent pendant le chargement,
  // plutôt que de faire clignoter des zéros qui se liraient comme une réalité.
  const stats = data?.getScormTrackingStats ?? previousData?.getScormTrackingStats

  const total = stats?.total ?? 0
  const percent = (value: number) => (total > 0 ? Math.round((value / total) * 100) : 0)

  const segments = [
    { key: 'completed', label: t('status.completed'), value: stats?.completed ?? 0, bar: 'bg-green-500', dot: 'bg-green-500' },
    { key: 'inProgress', label: t('status.incomplete'), value: stats?.inProgress ?? 0, bar: 'bg-yellow-500', dot: 'bg-yellow-500' },
    { key: 'notStarted', label: t('status.not_attempted'), value: stats?.notStarted ?? 0, bar: 'bg-muted-foreground/30', dot: 'bg-muted-foreground/30' },
    // Modules pas encore ouverts (#182), à part du « non commencé » : un module
    // qui ouvre le 15 septembre n'est pas du retard le 1er, et le fondre dans
    // le retard ferait passer une promo à jour pour une promo décrochée.
    { key: 'notYetUnlocked', label: t('stats.dashboard.notYetUnlocked'), value: stats?.notYetUnlocked ?? 0, bar: 'bg-sky-500/40', dot: 'bg-sky-500/40' },
    // N'apparaît que si un contenu n'a jamais posé de statut exploitable :
    // le fondre dans « non commencé » ferait passer un trou de données pour
    // un fait pédagogique.
    { key: 'unknown', label: t('status.unknown'), value: stats?.completionUnknown ?? 0, bar: 'bg-muted-foreground/60', dot: 'bg-muted-foreground/60' },
  ].filter(segment => !['unknown', 'notYetUnlocked'].includes(segment.key) || segment.value > 0)

  const evaluated = (stats?.passed ?? 0) + (stats?.failed ?? 0)
  const successRate = evaluated > 0 ? Math.round(((stats?.passed ?? 0) / evaluated) * 100) : null

  const tiles = [
    {
      key: 'averageScore',
      label: t('stats.dashboard.averageScore'),
      value: stats?.averageScorePercent != null ? `${stats.averageScorePercent} %` : '—',
      hint: stats?.averageScorePercent != null
        ? t('stats.dashboard.scoredOn', { scored: stats.scoredCount, total })
        : t('stats.dashboard.noScore'),
    },
    {
      key: 'successRate',
      label: t('stats.dashboard.successRate'),
      value: successRate != null ? `${successRate} %` : '—',
      hint: successRate != null
        ? t('stats.dashboard.successOn', { passed: stats?.passed ?? 0, evaluated })
        : t('stats.dashboard.noSuccess'),
    },
    {
      key: 'averageTime',
      label: t('stats.dashboard.averageTime'),
      value: stats?.averageTimeSeconds != null ? formatSeconds(stats.averageTimeSeconds) : '—',
      hint: stats?.averageTimeSeconds != null
        ? t('stats.dashboard.timedOn', { timed: stats.timedCount, total })
        : t('stats.dashboard.noTime'),
    },
  ]

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t('stats.dashboard.progressTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-3xl font-semibold tabular-nums">{total}</p>
          <p className="text-xs text-muted-foreground">{t('stats.dashboard.scope')}</p>

          {total === 0
            ? (
                <p className="text-sm text-muted-foreground">
                  {t('stats.dashboard.noEnrollments')}
                </p>
              )
            : (
                <>
                  {/* Barre empilée unique : trois proportions proches se comparent
                      mieux sur un axe commun que sur des secteurs de camembert. */}
                  <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
                    {segments.map(segment => (
                      <div
                        key={segment.key}
                        className={segment.bar}
                        style={{ width: `${(segment.value / total) * 100}%` }}
                        role="presentation"
                      />
                    ))}
                  </div>

                  <ul className="flex flex-wrap gap-x-6 gap-y-2">
                    {segments.map(segment => (
                      <li key={segment.key} className="flex items-center gap-2 text-sm">
                        <span className={`size-2.5 shrink-0 rounded-full ${segment.dot}`} />
                        <span>{segment.label}</span>
                        <span className="font-medium tabular-nums">{segment.value}</span>
                        <span className="text-muted-foreground tabular-nums">
                          {`(${percent(segment.value)} %)`}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* L'écart assumé avec la table : une ligne de progression
                      n'existe qu'au premier lancement du module, donc ces
                      inscrits comptent dans l'effectif sans y figurer. */}
                  {!!stats?.neverOpened && (
                    <p className="text-xs text-muted-foreground">
                      {t('stats.dashboard.neverOpened', { learners: stats.neverOpened })}
                    </p>
                  )}

                  {!!stats?.notYetUnlocked && (
                    <p className="text-xs text-muted-foreground">
                      {t('stats.dashboard.notYetUnlocked.hint', { modules: stats.notYetUnlocked })}
                    </p>
                  )}
                </>
              )}
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map(tile => (
          <Card key={tile.key}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {tile.label}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <p className="text-3xl font-semibold tabular-nums">{tile.value}</p>
              <p className="text-xs text-muted-foreground">{tile.hint}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

export default StatisticsDashboard
